import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Suspense, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { z } from 'zod';
import { endpointPcAnalyticsGET } from '~/api/def/endpoints/portfolioCompanyEndpoint';
import {
  endpointFundValuationRecalculatePOST,
  endpointPcValuationRecalculatePOST,
} from '~/api/def/endpoints/valuationEndpoint';
import AuditSection from '~/components/pages/companyPortfolio/AuditSection';
import DocumentsSection from '~/components/pages/companyPortfolio/DocumentsSection';
import OperationSection from '~/components/pages/companyPortfolio/OperationSection';
import PerformanceSection from '~/components/pages/companyPortfolio/PerformanceSection';
import RoundSection from '~/components/pages/companyPortfolio/RoundSection';
import { BqTabItem, BqTabs } from '~/components/ui/layout/bqTabs';
import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { formatCurrency } from '~/lib/money';
import { canAccess } from '~/utils/rbac';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

const documentSearchSchema = z.object({
  //TODO - do we really need the box types parameters on this page?
  mode: z.enum(['table', 'docOverview', 'docCompare']).optional(),
  docId: z.string().optional(),
  docIds: z.array(z.string()).optional(),
  pageStart: z.number().optional(),
  pageEnd: z.number().optional(),
  x1: z.number().optional(),
  y1: z.number().optional(),
  x2: z.number().optional(),
  y2: z.number().optional(),
  //fund name is used for path - the portfolio company name is used for the breadcrumb
  fundName: z.string().optional(),
  //generally we should be in context a fund here
  //optimally we should extract fund ID fund name from portfolio data
  fundId: z.string().optional(),
});

function PortfolioComponent() {
  const { portfolioId } = Route.useParams();
  const { fundId, fundName, ...restSearch } = Route.useSearch();
  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();

  const {
    data: portfolioAnalytics,
    isLoading,
    error,
  } = useSuspenseQuery({
    queryKey: ['pcAnalytics', portfolioId],
    queryFn: () => endpointPcAnalyticsGET(portfolioId),
  });

  // Update fundId in route if it's missing
  useEffect(() => {
    if (!fundId && portfolioAnalytics?.fundId) {
      navigate({
        search: {
          ...restSearch,
          fundId: portfolioAnalytics.fundId,
        },
      });
    }
  }, [fundId, portfolioAnalytics?.fundId, navigate, restSearch]);

  const recalculateValuationMutation = useMutation({
    mutationFn: async () => {
      // First recalculate portfolio valuation
      await endpointPcValuationRecalculatePOST(portfolioId);
      // Then recalculate fund valuation if fundId is present
      if (fundId) {
        await endpointFundValuationRecalculatePOST(fundId);
      }
    },
    onSuccess: () => {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Valuation recalculated successfully',
      });
      // Refresh the analytics data
      queryClient.invalidateQueries({ queryKey: ['pcAnalytics', portfolioId] });
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to recalculate valuation',
      });
    },
  });

  return (
    <div
      style={{
        height: '100vh',
        width: '100%',
      }}
    >
      <Toast ref={toast} />
      <div className="h-4rem">
        <HeaderContainer>
          {
            /**
             * handle both cases - within fund contex and outside fund context
             */
            fundId && fundName ? (
              <Breadcrumb
                items={[
                  { name: `Funds`, url: `/fund/overview` },
                  {
                    name: `Companies of ${fundName}`,
                    url: `/fund/portfolioCompanies/${fundId}?fundName=${fundName}`,
                  },
                  { name: portfolioAnalytics.name },
                ]}
              />
            ) : (
              <Breadcrumb
                items={[
                  { name: 'Companies ', url: '/portfolios' },
                  { name: portfolioAnalytics.name },
                ]}
              />
            )
          }
        </HeaderContainer>
      </div>

      <div
        className="p-4"
        style={{
          height: 'calc(100% - 4rem)',
        }}
      >
        <div className="grid text-sm">
          <div className="col text-center flex flex-column justify-content-center align-items-center">
            <p className="mb-2 mt-0 font-medium">Initial Investment</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal">
                {portfolioAnalytics.initialInvestment
                  ? formatCurrency(
                      portfolioAnalytics.currency ?? 'EUR',
                      portfolioAnalytics.initialInvestment,
                    )
                  : '-'}
              </h4>
            )}
          </div>

          <div className="col text-center flex flex-column justify-content-center align-items-center">
            <p className="mb-2 mt-0 font-medium">Invested to Date</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal">
                {portfolioAnalytics.investedToDate
                  ? formatCurrency(
                      portfolioAnalytics.currency ?? 'EUR',
                      portfolioAnalytics.investedToDate,
                    )
                  : '-'}
              </h4>
            )}
          </div>

          <div className="col text-center flex flex-column justify-content-center	align-items-center">
            <p className="mb-2 mt-0 font-medium">Current Stage</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal capitalize">
                {portfolioAnalytics.currentStage || '-'}
              </h4>
            )}
          </div>

          <div className="col text-center flex flex-column justify-content-center	align-content-center">
            <p className="mb-2 mt-0 font-medium">Sector</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal capitalize">
                {portfolioAnalytics.sector || '-'}
              </h4>
            )}
          </div>

          <div className="col text-center flex flex-column justify-content-center	align-content-center">
            <p className="mb-2 mt-0 font-medium">Geography</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal capitalize">
                {portfolioAnalytics.geography || '-'}
              </h4>
            )}
          </div>

          <div className="col text-center flex flex-column justify-content-center	align-content-center">
            <p className="mb-2 mt-0 font-medium">Status</p>
            {isLoading ? (
              <Skeleton width="4rem" height="1rem"></Skeleton>
            ) : (
              <h4 className="m-0 font-normal capitalize">
                {portfolioAnalytics.status || '-'}
              </h4>
            )}
          </div>
        </div>

        <BqTabs defaultIndex={1}>
          <BqTabItem label="Performance" index={1}>
            <PerformanceSection
              error={error}
              portfolioAnalytics={portfolioAnalytics}
              isLoading={isLoading}
              recalculateValuationMutation={recalculateValuationMutation}
            />
          </BqTabItem>
          {/* <BqTabItem label="Insights" index={2}>
            <InsightsSection />
          </BqTabItem> */}
          <BqTabItem label="Round" index={2}>
            <RoundSection portfolioCompanyId={portfolioAnalytics.id} />
          </BqTabItem>
          <BqTabItem label="Documents" index={3}>
            <DocumentsSection
              fundId={portfolioAnalytics.fundId}
              portfolioCompanyId={portfolioAnalytics.id}
            />
          </BqTabItem>
          <BqTabItem label="Audit" index={4}>
            <ErrorBoundary fallback={<div>Error loading audit sector</div>}>
              <AuditSection
                portfolioCompanyId={
                  portfolioId ?? '0194940d-fc87-708d-84d0-4767bdfa7591'
                }
              />
            </ErrorBoundary>
          </BqTabItem>
          <BqTabItem label="Operations" index={5}>
            <ErrorBoundary
              fallback={<div>Error loading operation section</div>}
            >
              <Suspense fallback={<div>Loading...</div>}>
                <OperationSection
                  portfolioCompanyId={
                    portfolioId ?? '0194940d-fc87-708d-84d0-4767bdfa7591'
                  }
                />
              </Suspense>
            </ErrorBoundary>
          </BqTabItem>
        </BqTabs>
      </div>
    </div>
  );
}

const PortfolioCompanyRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <PortfolioComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio-company/$portfolioId',
)({
  beforeLoad: async () => {
    if (!canAccess('portfolio')) {
      throw notFound();
    }
  },
  component: PortfolioCompanyRoute,
  validateSearch: documentSearchSchema,
});
